import React, { useState } from "react";
import axios from "axios";
import { FiSend, FiUsers, FiBell, FiAlertCircle } from "react-icons/fi";
import Swal from "sweetalert2";
import { getApiBaseUrl } from "../utils/apiConfig";
import "./AdminBroadcast.css";

const API = getApiBaseUrl();

export default function AdminBroadcast() {
  const [targetAudience, setTargetAudience] = useState("all");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  const [sending, setSending] = useState(false);

  const handleSendBroadcast = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      Swal.fire("Warning", "Please provide both a broadcast title and message.", "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: "Dispatch Broadcast Notification?",
      text: `This will send an in-app push notification to audience: [${targetAudience.toUpperCase()}].`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send Now",
      confirmButtonColor: "#3b82f6",
    });

    if (!confirm.isConfirmed) return;

    try {
      setSending(true);
      const token = localStorage.getItem("token") || localStorage.getItem("adminToken");
      const res = await axios.post(
        `${API}/admin/broadcast`,
        {
          target_audience: targetAudience,
          title,
          message,
          priority,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      Swal.fire({
        icon: "success",
        title: "Broadcast Dispatched!",
        text: res.data.message || `Sent to ${res.data.deliveredCount} recipients.`,
      });

      setTitle("");
      setMessage("");
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.response?.data?.message || "Failed to send broadcast", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="abc-container">
      <div className="abc-header">
        <div>
          <h2 className="abc-title">
            <FiBell className="abc-title-icon" /> Broadcast Announcement Desk
          </h2>
          <p className="abc-subtitle">
            Send real-time in-app notifications and official announcements to targeted user groups across SoloWay.
          </p>
        </div>
      </div>

      <div className="abc-layout">
        {/* Broadcast Form */}
        <div className="abc-card">
          <h3 className="abc-card-title">Compose Announcement</h3>
          <form onSubmit={handleSendBroadcast} className="abc-form">
            <div className="abc-field">
              <label className="abc-label">Target Audience</label>
              <div className="abc-audience-grid">
                {[
                  { id: "all", label: "All Users (Global)", icon: "🌐" },
                  { id: "hotel_owners", label: "Hotel Owners", icon: "🏨" },
                  { id: "guides", label: "Tour Guides", icon: "🧭" },
                  { id: "agents", label: "Travel Agents", icon: "🤝" },
                  { id: "travelers", label: "Travelers", icon: "🧳" },
                ].map((aud) => (
                  <button
                    type="button"
                    key={aud.id}
                    className={`abc-aud-btn ${targetAudience === aud.id ? "active" : ""}`}
                    onClick={() => setTargetAudience(aud.id)}
                  >
                    <span className="abc-aud-icon">{aud.icon}</span>
                    <span className="abc-aud-text">{aud.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="abc-field">
              <label className="abc-label">Broadcast Title</label>
              <input
                type="text"
                className="abc-input"
                placeholder="e.g. Platform Maintenance Notice / Peak Season Tips"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="abc-field">
              <label className="abc-label">Message Content</label>
              <textarea
                className="abc-textarea"
                rows="5"
                placeholder="Type your official announcement here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              ></textarea>
            </div>

            <div className="abc-row">
              <div className="abc-field" style={{ flex: 1 }}>
                <label className="abc-label">Priority Level</label>
                <select
                  className="abc-select"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  <option value="normal">Normal Priority</option>
                  <option value="high">High Priority</option>
                  <option value="urgent">Urgent Announcement</option>
                </select>
              </div>
            </div>

            <button type="submit" className="abc-submit-btn" disabled={sending}>
              <FiSend /> {sending ? "Dispatching..." : "Send Broadcast"}
            </button>
          </form>
        </div>

        {/* Info Box */}
        <div className="abc-info-card">
          <h4 className="abc-info-title">
            <FiAlertCircle /> Best Practices
          </h4>
          <ul className="abc-info-list">
            <li>Notifications appear immediately in the recipient's notification bell feed.</li>
            <li>Use <strong>Urgent</strong> priority for time-sensitive security alerts or scheduled maintenance.</li>
            <li>Targeted messages help increase host engagement and booking conversions without spamming travelers.</li>
          </ul>

          <div className="abc-preview-box">
            <div className="abc-preview-header">
              <FiUsers /> Live Preview
            </div>
            <div className="abc-preview-content">
              <div className="abc-preview-title">{title || "Your Announcement Title"}</div>
              <div className="abc-preview-body">{message || "Your announcement message body will be displayed here."}</div>
              <div className="abc-preview-badge">{targetAudience.toUpperCase()} AUDIENCE</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
